/**
 * Theme toggle component
 *
 * Provides a button to cycle through light/dark/system themes
 * with appropriate icons for each state.
 */
import { Moon, Sun, Monitor } from "lucide-react";
import { useTheme, type Theme } from "@/providers/theme-provider";
import { Button } from "@/components/ui/button";

// ---------------------------------------------------------------------------
// Pure utility functions
// ---------------------------------------------------------------------------

/**
 * Get the next theme in the cycle
 * light -> dark -> system -> light
 */
export function getNextTheme(current: Theme): Theme {
  const cycle: Theme[] = ["light", "dark", "system"];
  const currentIndex = cycle.indexOf(current);
  const nextIndex = (currentIndex + 1) % cycle.length;
  return cycle[nextIndex];
}

/**
 * Get accessible label for theme
 */
export function getThemeLabel(theme: Theme): string {
  switch (theme) {
    case "light":
      return "Light mode";
    case "dark":
      return "Dark mode";
    case "system":
      return "System theme";
  }
}

/**
 * Get icon component for theme
 */
export function getThemeIcon(
  theme: Theme,
  resolvedTheme: "light" | "dark"
): typeof Sun | typeof Moon | typeof Monitor {
  if (theme === "system") {
    return Monitor;
  }
  // When theme is explicitly set, show the current resolved theme icon
  return resolvedTheme === "dark" ? Moon : Sun;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface ThemeToggleProps {
  /** Additional CSS classes */
  className?: string;
}

/**
 * Theme toggle button
 *
 * Cycles through light -> dark -> system themes.
 * Shows appropriate icon for current state.
 */
export function ThemeToggle({ className }: ThemeToggleProps) {
  const { theme, resolvedTheme, setTheme } = useTheme();

  const handleClick = () => {
    setTheme(getNextTheme(theme));
  };

  const Icon = getThemeIcon(theme, resolvedTheme);
  const label = getThemeLabel(theme);

  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={handleClick}
      className={className}
      aria-label={`Current: ${label}. Click to change theme.`}
      title={label}
    >
      <Icon className="h-5 w-5" />
      <span className="sr-only">{label}</span>
    </Button>
  );
}
